import "./FincaAsignarClienteModal.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { DuenoFincaAsignadoResponse, DuenoFincaMapa, FincaMapaResponse } from "../../../shared/response/terceros/FincaMapaResponse";
import { ClienteResponse } from "../../../shared/response/terceros/ClienteResponse";
import { FincaAsignarClientePayload } from "../../../shared/services/api/payload/terceros/FincaAsignarClientePayload";
import { ClienteService, buscarClientes } from "../../services/terceros/ClienteService";
import { FincaAsignarClienteService } from "../../services/terceros/FincaAsignarClienteService";

/**
 * Asigna una finca del mapa a un cliente más (`AsignarCliente`). Buscador de clientes en el navegador, sobre la lista
 * de `ListClientes` (`buscarClientes`; a un promotor le llega solo su cartera), y el nombre con el que ese cliente verá
 * la finca, prellenado con el que ya tiene. Los dueños actuales salen en la lista pero no se pueden elegir.
 */
export class FincaAsignarClienteModal extends BaseSmallModalFormComponent<
  DuenoFincaAsignadoResponse,
  FincaAsignarClientePayload,
  FincaAsignarClienteService
> {
  private _cliente: ClienteResponse | null = null;
  /** Clientes donde se busca: la lista de `ListClientes`, pedida al abrir la ventana. */
  private _clientes: ClienteResponse[] = [];
  private _resultados: ClienteResponse[] = [];
  private _search!: HTMLInputElement;
  private _results!: HTMLElement;
  private _nombre!: HTMLInputElement;

  /** Recibe la finca y a quién avisar con el dueño nuevo. */
  constructor(
    private readonly _finca: FincaMapaResponse,
    private readonly _onAsignado: (dueno: DuenoFincaMapa) => void,
  ) {
    super(new FincaAsignarClienteService());
  }

  protected get title(): string { return this._finca.duenos.length > 0 ? "Asignar a otro cliente" : "Asignar a un cliente"; }
  protected get saveText(): string { return "Asignar"; }

  /** Finca, buscador de cliente y nombre de la finca para ese cliente (sin cliente: el último que tuvo). */
  protected renderBody(): string {
    const nombres = this._finca.duenos.length > 0
      ? [...new Set(this._finca.duenos.map((d) => d.nombreFinca))]
      : this._finca.nombreUltimo ? [this._finca.nombreUltimo] : [];
    return `
      <div class="mf-asig-finca"><i class="ph ph-map-pin"></i> ${esc(nombres.join(" / ") || "Finca sin nombre")}</div>

      <label class="cl-form-label" for="mf-asig-search">Cliente</label>
      <div class="mf-asig-search-wrap">
        <input id="mf-asig-search" class="sx-input" type="search" autocomplete="off" placeholder="Escribe NIT o nombre…" />
        <div class="mf-asig-results" id="mf-asig-results" hidden></div>
      </div>

      <label class="cl-form-label mf-asig-label" for="mf-asig-nombre">Nombre de la finca para este cliente</label>
      <input id="mf-asig-nombre" class="sx-input" type="text" maxlength="500" value="${esc(nombres[0] ?? "")}" />
      <div class="mf-asig-hint">Cada cliente le pone su propio nombre a la finca y no puede repetirlo entre sus fincas. La finca es la misma: comparten AOI, lotes y datos.</div>
    `;
  }

  /**
   * Conecta el buscador y la validación, y pide la lista de clientes donde se busca. Si se escribió algo antes de que
   * llegue la lista, busca de nuevo al recibirla.
   */
  protected override onAfterRender(el: HTMLDivElement): void {
    this._search = el.querySelector<HTMLInputElement>("#mf-asig-search")!;
    this._results = el.querySelector<HTMLElement>("#mf-asig-results")!;
    this._nombre = el.querySelector<HTMLInputElement>("#mf-asig-nombre")!;

    this._search.addEventListener("input", () => this._onSearch());
    // Cerrar resultados al perder foco, con espera para que alcance a registrar el mousedown del ítem.
    this._search.addEventListener("blur", () => setTimeout(() => { this._results.hidden = true; }, 150));
    this._nombre.addEventListener("input", () => this._validar());
    this._search.focus();

    void ClienteService.instance.get().then((clientes) => {
      this._clientes = clientes;
      if (this._search.value.trim() !== "" && !this._cliente) this._onSearch();
    });
  }

  /**
   * Busca al momento en la lista de clientes (`buscarClientes`, en el navegador, hasta 10, sin mínimo de letras);
   * escribir invalida la selección anterior.
   */
  private _onSearch(): void {
    this._cliente = null;
    this._validar();
    if (this._search.value.trim() === "") { this._results.hidden = true; return; }
    this._resultados = buscarClientes(this._clientes, this._search.value, 10);
    this._pintarResultados();
  }

  /** Lista de coincidencias; los dueños actuales aparecen marcados y no se pueden elegir. */
  private _pintarResultados(): void {
    const duenos = new Set(this._finca.duenos.map((d) => d.clienteId));
    this._results.innerHTML = this._resultados.length === 0
      ? `<div class="mf-asig-empty">Sin coincidencias</div>`
      : this._resultados.map((c) => `
          <div class="mf-asig-item${duenos.has(c.id) ? " is-dueno" : ""}" data-id="${esc(c.id)}">
            <span class="mf-asig-name">${esc(c.nombre)}</span>
            <span class="mf-asig-nit">${duenos.has(c.id) ? "Ya es dueño de esta finca" : `NIT ${esc(c.nit)}`}</span>
          </div>`).join("");
    this._results.hidden = false;
    this._results.querySelectorAll<HTMLElement>(".mf-asig-item:not(.is-dueno)").forEach((item) => {
      // mousedown (no click) para que corra antes del blur del input.
      item.addEventListener("mousedown", (e) => {
        e.preventDefault();
        const c = this._resultados.find((x) => x.id === item.dataset.id);
        if (!c) return;
        this._cliente = c;
        this._search.value = `${c.nombre} · NIT ${c.nit}`;
        this._results.hidden = true;
        this._validar();
      });
    });
  }

  /** Habilita "Asignar" con cliente elegido y nombre no vacío. */
  private _validar(): void {
    this.setSaveDisabled(!this._cliente || this._nombre.value.trim().length === 0);
  }

  /** Finca + cliente elegido + nombre. */
  protected buildPayload(): FincaAsignarClientePayload {
    return { id: this._finca.id, clienteId: this._cliente!.id, nombre: this._nombre.value.trim() };
  }

  /** Avisa al mapa con el dueño nuevo y cierra. */
  protected override onSaved(item: DuenoFincaAsignadoResponse): void {
    this._onAsignado({ clienteId: item.clienteId, clienteNombre: item.clienteNombre, nombreFinca: item.nombreFinca });
    this.dispose();
  }
}

/** Escapa texto para insertarlo en HTML. */
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
